@use '@/styles/tokens' as *;

// Warranty Claims list — Figma 430:394247, panel 430:408464, rows 430:410062 / 430:410063;
// empty state Figma 430:391999, node 430:406915. The panel chrome (cream header band, BG Pattern,
// 24px body padding, CloseButton, width 464) belongs to <Drawer> and is not restyled here — see
// plan.md § Design spec § Accepted deviations.

.list {
  display: flex;
  flex-direction: column;
  // Figma row-to-row gap: rows at y 0 / 111 / 222 / 333 over a 99px box.
  gap: var(--space-3);
  margin: 0;
  padding: 0;
  list-style: none;
}

// The row is a <button> inside its <li> — the whole row opens the Ticket Preview, so the mouse and
// the keyboard get the same target. `reset-button` comes FIRST on purpose: it zeroes `padding` and
// `border`, so the Figma padding and divider below have to be declared after it to survive.
.row {
  @include reset-button;
  display: flex;
  flex-direction: column;
  // Figma gap between the identity block and the date line.
  gap: var(--space-3);
  // The button must fill its <li> so the row renders exactly as it did when the <li> was the row.
  width: 100%;
  // Figma `py-[14px]`. No 14px step exists in the space scale (4/8/12/16/…) and inventing one for
  // a single row is worse than the literal.
  padding: 14px 0;
  text-align: start;
  border-bottom: 1px solid var(--border-subtle);

  @include focus-ring;

  // Figma draws no divider under the last row (node 430:410105 carries no border-b). The border now
  // sits on the button, so the test is on its <li>: a bare `&:last-child` would match *every* row,
  // each button being the only child of its own list item.
  li:last-child > & {
    border-bottom: none;
  }
}

.rowTop {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-2);
}

.identity {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.title {
  font-size: $font-size-16;
  font-weight: $font-weight-medium;
  color: var(--text-primary);
  overflow-wrap: anywhere;
}

.subtitle {
  font-size: $font-size-13;
  font-weight: $font-weight-regular;
  color: var(--text-muted);
  overflow-wrap: anywhere;
}

.date {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  font-size: $font-size-13;
  // The CalendarDots glyph inherits currentColor; Figma strokes it #555555, same as the text.
  color: var(--text-tertiary);
}

// "No claims found" and the load-failure state share this centred well.
.state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-4);
  height: 100%;
  min-height: 240px;
}

.emptyIcon {
  // Figma "stone" #343330 snapped to the ramp; the exported glyph carries opacity 0.3.
  color: var(--text-secondary);
  opacity: 0.3;
}

.emptyText {
  margin: 0;
  font-size: $font-size-14;
  font-weight: $font-weight-medium;
  color: var(--text-primary);
}

.stateText {
  margin: 0;
  font-size: $font-size-14;
  color: var(--text-muted);
  text-align: center;
}

// No Figma frame covers the skeleton (context.md discrepancy #24), so it reuses the shimmer the
// device grid already ships, sized to a real row (14 + 42 + 12 + 17 + 14 = 99px) so nothing
// reflows when the first page lands.
.skeleton {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.skeletonRow {
  height: 99px;
  border-radius: $radius-lg;
  background-color: var(--surface-muted);
  @include shimmer;
}

.sentinel {
  height: 1px;
}

.nextPage {
  padding-top: var(--space-3);
}
